import { Modal } from "antd";
import { useAppDispatch, useAppSelector } from "core/utils";
import { type FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import styled from "styled-components";

import type { DataGroupEntity } from "src/app/features/DataGroup/types/DataGroupEntity";
import { getAccessToken, getAssignedDataGroup, getUserNameFromToken, setAssignedDataGroup } from "../../../core/router/auth";
import { getIdToken, setUserRights } from "../../../core/router/auth/SecurityManager";
import { routes } from "../../../core/router/config/routes";
import { getBeBaseUrl } from "../../../core/setup/config/utils";
import { closeChangeDataGroup } from "../../../core/setup/redux/state/dataGroupModal/dataGroupModalState";
import { Spaces } from "../../../core/styles/theme";
import type { NoneOwnProps } from "../../../core/types";
import { PrimaryButton, SecondaryButton } from "../../Buttons/components/ActionButton";
import { showErrorNotification } from "../../NotificationAlerts";
import { DataGroupSelector } from "./DataGroupSelector";

export const ChooseDataGroupModal: FC<NoneOwnProps> = () => {
    const userName = getUserNameFromToken();
    const { t } = useTranslation();
    const [newSelectedDataGroup, setNewSelectedDataGroup] = useState<DataGroupEntity>(getAssignedDataGroup());

    const isModalVisible = useAppSelector((state) => state.dataGroup.isChangeDataGroupVisible);

    const dispatch = useAppDispatch();

    const navigate = useNavigate();
    const baseUrl = getBeBaseUrl();

    // eslint-disable-next-line no-underscore-dangle
    const isMSEntraLogin = window.__RUNTIME_CONFIG__?.IDENTITY_SERVICE === "entra";
    let token;
    if (isMSEntraLogin) {
        token = getIdToken();
    } else {
        token = getAccessToken();
    }

    const handleSave = async () => {
        if (newSelectedDataGroup) {
            try {
                const response = await fetch(
                    `${baseUrl}switch-data-group?dataGroupCode=${encodeURIComponent(newSelectedDataGroup.code ?? "")}`,
                    {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${token}`,
                        },
                    },
                );

                if (!response.ok) {
                    throw new Error(`Failed to switch data group: ${response.status}`);
                }

                const data = await response.json();
                const { userRights } = data;
                setUserRights(userRights);
                setAssignedDataGroup(newSelectedDataGroup);
                dispatch(closeChangeDataGroup());
                // Hard navigation avoids reloading the stale edit-form URL (SOLUTIONS-8402).
                window.location.href = routes.dashboard.path;
            } catch {
                // Backend didn't return a valid response (e.g. an outage) - surface a generic error
                // instead of letting the raw fetch/parse failure crash the handler silently.
                dispatch(showErrorNotification({ text: t("common.error"), autoCloseDisabled: true }));
            }
        }
    };

    const handleClose = () => {
        dispatch(closeChangeDataGroup());
    };

    const handleSettings = () => {
        dispatch(closeChangeDataGroup());
        navigate(routes.dataGroups.path);
    };

    return (
        <Modal
            open={isModalVisible}
            title={t("dataGroup.selectDataGroup")}
            cancelText={t("common.close")}
            footer={null}
            closable
            onCancel={handleClose}
        >
            <MainWrapper data-name="data_group_selector_popup">
                <p>{t("dataGroup.multipleGroupAssigned", { email: userName })}</p>
                <DataGroupSelector onChange={setNewSelectedDataGroup} value={newSelectedDataGroup} />
                <ButtonWrapper>
                    <SecondaryButton onClick={() => handleSettings()}>{t("common.settings")}</SecondaryButton>
                    <PrimaryButton name="save" onClick={() => handleSave()}>
                        {t("common.save")}
                    </PrimaryButton>
                </ButtonWrapper>
            </MainWrapper>
        </Modal>
    );
};

const MainWrapper = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    gap: ${Spaces.DefaultPadding};
`;

const ButtonWrapper = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    gap: ${Spaces.SmallPadding};
    padding-top: ${Spaces.LargePadding};
`;
